import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import {
  exportCompaniesTo1C,
  getCompanyStatus,
  setCompanyStatus,
  deleteCompanyStatus,
  getCompanyIdentifier,
} from '../services/api';
import {
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  AddCircleOutline as AddStatusIcon,
  KeyboardArrowDown as ArrowDownIcon,
  RemoveCircleOutline as RemoveStatusIcon,
} from '@mui/icons-material';
import ManagerSelector from './ManagerSelector';
import { STATUS_CONFIG } from './CompanyCard';
import { useGlobalNotification } from '../App';
import { createClickableAddressData } from '../utils/mapUtils';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCompanyByIndex, searchMetadata } = useCompany();
  const { showNotification, showSuccess } = useGlobalNotification();
  const company = getCompanyByIndex(id);
  const [activeTab, setActiveTab] = useState('basic');
  const [openAccordions, setOpenAccordions] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [managerSelectorOpen, setManagerSelectorOpen] = useState(false);

  // Состояние смены статуса прямо в карточке компании
  const [companyStatus, setCompanyStatusState] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusCommentOpen, setStatusCommentOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusComment, setStatusComment] = useState('');
  const [isSavingStatus, setIsSavingStatus] = useState(false);

  const companyIdObj = company ? getCompanyIdentifier(company) : null;
  const statusIdentifier = companyIdObj?.value || null;
  const statusInfo = companyStatus?.status ? STATUS_CONFIG[companyStatus.status] : null;

  // Подгружаем текущий статус компании при открытии карточки
  useEffect(() => {
    if (!statusIdentifier) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getCompanyStatus(statusIdentifier);
        if (!cancelled) setCompanyStatusState(data);
      } catch (err) {
        console.error('Не удалось загрузить статус компании:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statusIdentifier]);

  // Слушаем глобальное событие, чтобы статус оставался актуальным
  // (например, после экспорта в 1С статус автоматически становится 'exported_to_crm')
  useEffect(() => {
    if (!statusIdentifier) return;
    const handler = (e) => {
      const d = e.detail || {};
      const matches =
        d.companyIdentifier === statusIdentifier ||
        (company?.inn && d.companyInn === company.inn) ||
        (company?.internal_id && d.companyInternalId === company.internal_id);
      if (!matches) return;
      if (d.status) {
        setCompanyStatusState({
          status: d.status,
          comment: d.comment || '',
          updated_at: new Date().toISOString(),
        });
      } else {
        setCompanyStatusState(null);
      }
    };
    window.addEventListener('exportStatusChanged', handler);
    return () => window.removeEventListener('exportStatusChanged', handler);
  }, [statusIdentifier, company?.inn, company?.internal_id]);

  const handleStatusButtonClick = (event) => {
    event.stopPropagation();
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusSelect = (statusKey) => {
    setSelectedStatus(statusKey);
    setStatusComment(companyStatus?.comment || '');
    setStatusCommentOpen(true);
    handleStatusMenuClose();
  };

  const handleSaveStatus = useCallback(async () => {
    if (!statusIdentifier || !selectedStatus) return;
    setIsSavingStatus(true);
    try {
      const commentToSend = statusComment.trim() || '';
      await setCompanyStatus(statusIdentifier, selectedStatus, commentToSend);

      setCompanyStatusState({
        status: selectedStatus,
        comment: commentToSend,
        updated_at: new Date().toISOString(),
      });

      window.dispatchEvent(
        new CustomEvent('exportStatusChanged', {
          detail: {
            companyIdentifier: statusIdentifier,
            companyInn: company?.inn,
            companyInternalId: company?.internal_id,
            status: selectedStatus,
            comment: commentToSend,
          },
        })
      );

      setStatusCommentOpen(false);
      setSelectedStatus(null);
      setStatusComment('');
    } catch (error) {
      console.error('Ошибка сохранения статуса:', error);
      setNotification({
        open: true,
        message:
          'Ошибка сохранения статуса: ' +
          (error.response?.data?.detail || error.message || 'неизвестная ошибка'),
        severity: 'error',
      });
    } finally {
      setIsSavingStatus(false);
    }
  }, [statusIdentifier, selectedStatus, statusComment, company?.inn, company?.internal_id]);

  const handleRemoveStatus = useCallback(async () => {
    if (!statusIdentifier) return;
    handleStatusMenuClose();
    setIsSavingStatus(true);
    try {
      await deleteCompanyStatus(statusIdentifier);
      setCompanyStatusState(null);
      window.dispatchEvent(
        new CustomEvent('exportStatusChanged', {
          detail: {
            companyIdentifier: statusIdentifier,
            companyInn: company?.inn,
            companyInternalId: company?.internal_id,
            status: null,
            comment: null,
          },
        })
      );
    } catch (error) {
      console.error('Ошибка снятия статуса:', error);
      setNotification({
        open: true,
        message:
          'Ошибка снятия статуса: ' +
          (error.response?.data?.detail || error.message || 'неизвестная ошибка'),
        severity: 'error',
      });
    } finally {
      setIsSavingStatus(false);
    }
  }, [statusIdentifier, company?.inn, company?.internal_id]);

  if (!company) {
    return (
      <div className="container">
        <h1>Компания не найдена</h1>
        <button className="back-button" onClick={() => navigate('/companies')}>
          ← Вернуться к списку
        </button>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const formatCoordinates = (coords) => {
    if (Array.isArray(coords) && coords.length === 2) {
      return `${coords[0]}, ${coords[1]}`;
    }
    return 'Не указаны';
  };

  const toggleAccordion = (id) => {
    setOpenAccordions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Helper function to extract company data from both old and new formats
  const getCompanyData = (company) => {
    if (!company) return { phone: null, hours: null, categories: [] };

    // Try to get data from top-level first (old format)
    let phone = null;
    let hours = company.working_hours || null;
    let categories = company.categories || [];

    // Get phone from top-level phone_numbers
    if (company.phone_numbers && company.phone_numbers.length > 0) {
      phone = company.phone_numbers[0];
    }

    // If no data at top level, try to get from stores[0] (new format with virtual store)
    if (company.stores && company.stores.length > 0) {
      const store = company.stores[0];

      // Use store data if top-level data is missing
      if (!phone && store.phone_numbers && store.phone_numbers.length > 0) {
        phone = store.phone_numbers[0];
      }
      if (!hours && store.working_hours) {
        hours = store.working_hours;
      }
      if (categories.length === 0 && store.categories && store.categories.length > 0) {
        categories = store.categories;
      }
    }

    // Also try contacts[0] for phone if still not found
    if (!phone && company.contacts && company.contacts.length > 0) {
      const contact = company.contacts[0];
      if (contact.phone_numbers && contact.phone_numbers.length > 0) {
        phone = contact.phone_numbers[0];
      }
    }

    // Fallback: phone from website_info.contact_info
    if (!phone && company.website_info?.contact_info?.phone) {
      phone = company.website_info.contact_info.phone;
    }

    return { phone, hours, categories };
  };

  const handleExportTo1C = () => {
    // Сначала открываем диалог выбора менеджера
    setManagerSelectorOpen(true);
  };

  const performExportTo1C = async (selectedManager) => {
    let taskId = null;

    // ПРИОРИТЕТ 1: Из URL параметров (самый надежный)
    const urlParams = new URLSearchParams(window.location.search);
    taskId = urlParams.get('task_id');

    // ПРИОРИТЕТ 2: Из метаданных контекста (связанные с текущими результатами)
    if (!taskId && searchMetadata?.task_id) {
      taskId = searchMetadata.task_id;
    }

    // ПРИОРИТЕТ 3: Из текущего URL если мы на /results/:taskId
    if (!taskId && window.location.pathname.includes('/results/')) {
      const match = window.location.pathname.match(/\/results\/([^/]+)/);
      if (match) {
        taskId = match[1];
      }
    }

    // ПРИОРИТЕТ 4: Из referrer если пришли с /results/:taskId
    if (!taskId && document.referrer) {
      const match = document.referrer.match(/\/results\/([^/]+)/);
      if (match) {
        taskId = match[1];
      }
    }

    // ПРИОРИТЕТ 5: Последний шанс из sessionStorage (НО не перезаписываем!)
    if (!taskId) {
      taskId = sessionStorage.getItem('current_task_id');
    }



    if (!taskId) {
      setNotification({
        open: true,
        message: 'Ошибка: Не удалось определить идентификатор задачи для экспорта. Экспорт доступен только для компаний из сохраненных результатов поиска.',
        severity: 'error'
      });
      return;
    }

    try {
      setIsExporting(true);

      // Передаем ИНН конкретной компании для фильтрации
      const result = await exportCompaniesTo1C(taskId, selectedManager.id, company.inn);



      if (result.success) {
        // Бэкенд автоматически устанавливает статус 'exported_to_crm'
        // Уведомляем все компоненты об изменении статуса экспорта
        window.dispatchEvent(new CustomEvent('exportStatusChanged', {
          detail: {
            companyInn: company.inn,
            companyName: company.name,
            status: 'exported_to_crm',
            comment: `Экспортировано в CRM. Менеджер: ${selectedManager.name}`
          }
        }));

        // Используем глобальную систему для успешного экспорта
        showSuccess(
          `Успешный экспорт в 1С! Экспортировано компаний: ${result.companies_count || 'неизвестно'}. Менеджер: ${selectedManager.name}. Время экспорта: ${new Date().toLocaleString()}`
        );
      } else {
        // Проверяем, есть ли структурированные ошибки в предупреждении
        const hasStructuredErrors = result.message &&
          (result.message.includes('ErrorsList') || result.message.includes('ERROR!!!'));

        if (hasStructuredErrors) {
          // Пытаемся извлечь JSON с ошибками из сообщения
          try {
            const jsonMatch = result.message.match(/\{.*\}/s);
            if (jsonMatch) {
              const errorData = JSON.parse(jsonMatch[0]);

              // Создаем объект ошибки для ErrorNotification
              const structuredError = {
                response: {
                  data: errorData
                }
              };

              showNotification(structuredError, 'warning');
            } else {
              // Fallback к обычному уведомлению
              setNotification({
                open: true,
                message: `Экспорт завершен с предупреждениями: ${result.message || 'Проверьте логи системы'}`,
                severity: 'warning'
              });
            }
          } catch (parseError) {
            console.warn('Failed to parse error JSON from message:', parseError);
            // Fallback к обычному уведомлению
            setNotification({
              open: true,
              message: `Экспорт завершен с предупреждениями: ${result.message || 'Проверьте логи системы'}`,
              severity: 'warning'
            });
          }
        } else {
          // Обычное предупреждение без структурированных ошибок
          setNotification({
            open: true,
            message: `Экспорт завершен с предупреждениями: ${result.message || 'Проверьте логи системы'}`,
            severity: 'warning'
          });
        }
      }
    } catch (error) {
      console.error('Ошибка экспорта в 1С:', error);

      // Используем глобальную систему уведомлений
      showNotification(error, 'auto');
    } finally {
      setIsExporting(false);
    }
  };

  const handleManagerSelectorClose = () => {
    setManagerSelectorOpen(false);
  };

  const handleManagerSelectorConfirm = (selectedManager) => {
    setManagerSelectorOpen(false);
    performExportTo1C(selectedManager);
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  const cleanMarkdown = (text) => {
    if (!text) return text;
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Убираем **bold**
      .replace(/\*(.*?)\*/g, '$1')     // Убираем *italic*
      .replace(/#{1,6}\s*(.*)/g, '$1') // Убираем заголовки
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Убираем ссылки
      .replace(/`(.*?)`/g, '$1')       // Убираем код
      .replace(/^\s*[*\-+]\s+/gm, '') // Убираем маркеры списков
      .replace(/^\s*\d+\.\s+/gm, '')   // Убираем нумерацию
      .trim();
  };


  const AccordionItem = ({ id, title, children, isOpen, defaultOpen = false }) => {
    const isAccordionOpen = openAccordions[id] !== undefined ? openAccordions[id] : defaultOpen;

    return (
      <div className="accordion-item">
        <div
          className={`accordion-header ${isAccordionOpen ? 'open' : ''}`}
          onClick={() => toggleAccordion(id)}
        >
          <h4>{title}</h4>
          <span className="accordion-icon">
            {isAccordionOpen ? '−' : '+'}
          </span>
        </div>
        <div className={`accordion-content ${isAccordionOpen ? 'open' : ''}`}>
          <div className="accordion-body">
            {children}
          </div>
        </div>
      </div>
    );
  };

  const renderAddress = (address) => {
    if (!address) return 'Не указан';

    const addressData = createClickableAddressData(
      address,
      company.coordinates && company.coordinates.length === 2
        ? { lat: company.coordinates[0], lon: company.coordinates[1] }
        : null
    );

    if (typeof address === 'string') {
      return addressData.isClickable ? (
        <a
          href={addressData.link}
          target="_blank"
          rel="noopener noreferrer"
          className="clickable-address-link"
          title="Открыть на Яндекс.Картах"
          style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
          {addressData.text}
        </a>
      ) : (
        addressData.text
      );
    }

    // Для объекта адреса
    const fullAddressText = address.full_address || `${address.region || ''} ${address.city || ''} ${address.street || ''} ${address.house || ''}`.trim();
    const fullAddressData = createClickableAddressData(fullAddressText);

    return (
      <div>
        {address.full_address && (
          <div>
            <strong>Полный адрес:</strong>{' '}
            {fullAddressData.isClickable ? (
              <a
                href={fullAddressData.link}
                target="_blank"
                rel="noopener noreferrer"
                className="clickable-address-link"
                title="Открыть на Яндекс.Картах"
                style={{ color: '#1976d2', textDecoration: 'underline' }}
              >
                {address.full_address}
              </a>
            ) : (
              address.full_address
            )}
          </div>
        )}
        {address.city && <div><strong>Город:</strong> {address.city}</div>}
        {address.street && <div><strong>Улица:</strong> {address.street}</div>}
        {address.house && <div><strong>Дом:</strong> {address.house}</div>}
        {address.postal_code && <div><strong>Почтовый индекс:</strong> {address.postal_code}</div>}
        {address.region && <div><strong>Регион:</strong> {address.region}</div>}
        {address.country && <div><strong>Страна:</strong> {address.country}</div>}
      </div>
    );
  };

  const renderLegalInfo = (legalInfo) => {
    if (!legalInfo) return null;

    return (
      <div className="detail-section">
        <h3>📋 Юридическая информация</h3>
        <div className="detail-grid">
          {legalInfo.inn && (
            <div className="detail-item">
              <div className="detail-label">ИНН</div>
              <div className="detail-value">{legalInfo.inn}</div>
            </div>
          )}
          {legalInfo.ogrn && (
            <div className="detail-item">
              <div className="detail-label">ОГРН</div>
              <div className="detail-value">{legalInfo.ogrn}</div>
            </div>
          )}
          {legalInfo.kpp && (
            <div className="detail-item">
              <div className="detail-label">КПП</div>
              <div className="detail-value">{legalInfo.kpp}</div>
            </div>
          )}
          {legalInfo.organizational_form && (
            <div className="detail-item">
              <div className="detail-label">Организационная форма</div>
              <div className="detail-value">{legalInfo.organizational_form}</div>
            </div>
          )}
          {legalInfo.company_status && (
            <div className="detail-item">
              <div className="detail-label">Статус компании</div>
              <div className={`detail-value ${legalInfo.company_status === 'Действующая' ? 'status-active' : 'status-inactive'}`}>
                {legalInfo.company_status}
              </div>
            </div>
          )}
          {legalInfo.registration_date && (
            <div className="detail-item">
              <div className="detail-label">Дата регистрации</div>
              <div className="detail-value">{formatDate(legalInfo.registration_date)}</div>
            </div>
          )}
          {legalInfo.liquidation_date && (
            <div className="detail-item">
              <div className="detail-label">Дата ликвидации</div>
              <div className="detail-value">{formatDate(legalInfo.liquidation_date)}</div>
            </div>
          )}
          {legalInfo.authorized_capital && (
            <div className="detail-item">
              <div className="detail-label">Уставный капитал</div>
              <div className="detail-value">{legalInfo.authorized_capital}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCatalogData = (catalogData) => {
    if (!catalogData || !catalogData.length) return null;

    // Собираем все товары из всех каталогов
    const allProducts = catalogData.reduce((acc, catalog) => {
      if (catalog.result?.result?.products) {
        return [...acc, ...catalog.result.result.products];
      }
      return acc;
    }, []);

    // Получаем уникальные категории
    const categories = ['all', ...new Set(allProducts.map(product => product.category))];

    // Фильтруем товары по выбранной категории
    const filteredProducts = selectedCategory === 'all'
      ? allProducts
      : allProducts.filter(product => product.category === selectedCategory);

    return (
      <AccordionItem id="catalog" title={`🛒 Каталог товаров (${allProducts.length})`}>
        {/* Фильтр по категориям */}
        <div className="catalog-filters">
          <h5>Фильтр по категориям:</h5>
          <div className="category-filters">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === 'all' ? 'Все товары' : category}
                {category === 'all'
                  ? ` (${allProducts.length})`
                  : ` (${allProducts.filter(p => p.category === category).length})`
                }
              </button>
            ))}
          </div>
        </div>

        {/* Список товаров */}
        <div className="products-grid">
          {filteredProducts.slice(0, 50).map((product, idx) => {
            const name = product.productName || product.name || '';
            return (
              <div key={idx} className="product-card">
                <div className="product-name">{name}</div>
                {product.price && <div className="product-price">{product.price}</div>}
                {product.category && <div className="product-category">{product.category}</div>}
                {product.description && product.description !== name && (
                  <div className="product-description">{product.description}</div>
                )}
              </div>
            );
          })}
        </div>

        {filteredProducts.length > 50 && (
          <div className="products-note">
            Показано первые 50 товаров из {filteredProducts.length}
          </div>
        )}
      </AccordionItem>
    );
  };

  const renderWebsiteInfo = (websiteInfo, catalogParsingResults) => {
    // Если website_info пуст, но есть catalog_parsing_results с URL — строим минимальный объект
    if (!websiteInfo && catalogParsingResults?.website_url) {
      websiteInfo = { url: catalogParsingResults.website_url };
    }
    if (!websiteInfo) return null;

    return (
      <div className="detail-section">
        <h3>🌐 Информация с сайта</h3>

        {/* Каталог товаров из raw_catalog_data */}
        {renderCatalogData(websiteInfo.raw_catalog_data)}

        {/* Каталог товаров из catalog_parsing_results.all_products */}
        {!websiteInfo.raw_catalog_data && catalogParsingResults?.all_products?.length > 0 && (
          <AccordionItem id="catalog-fallback" title={`🛒 Каталог товаров (${catalogParsingResults.all_products.length})`}>
            <div className="products-grid">
              {catalogParsingResults.all_products.slice(0, 50).map((product, idx) => {
                const name = product.productName || product.name || '';
                return (
                  <div key={idx} className="product-card">
                    <div className="product-name">{name}</div>
                    {product.price && <div className="product-price">{product.price}</div>}
                    {product.category && <div className="product-category">{product.category}</div>}
                    {product.description && product.description !== name && (
                      <div className="product-description">{product.description}</div>
                    )}
                  </div>
                );
              })}
            </div>
            {catalogParsingResults.all_products.length > 50 && (
              <div className="products-note">
                Показано первые 50 товаров из {catalogParsingResults.all_products.length}
              </div>
            )}
          </AccordionItem>
        )}

        {/* Основная информация */}
        <AccordionItem id="website-basic" title="📋 Основная информация о сайте">
          <div className="detail-grid">
            {websiteInfo.url && (
              <div className="detail-item">
                <div className="detail-label">URL</div>
                <div className="detail-value">
                  <a href={websiteInfo.url} target="_blank" rel="noopener noreferrer">
                    {websiteInfo.url}
                  </a>
                </div>
              </div>
            )}
            {websiteInfo.title && (
              <div className="detail-item">
                <div className="detail-label">Заголовок сайта</div>
                <div className="detail-value">{websiteInfo.title}</div>
              </div>
            )}
            {websiteInfo.description && (
              <div className="detail-item">
                <div className="detail-label">Описание</div>
                <div className="detail-value">{websiteInfo.description}</div>
              </div>
            )}
            {websiteInfo.has_online_store !== undefined && (
              <div className="detail-item">
                <div className="detail-label">Интернет-магазин</div>
                <div className="detail-value">{websiteInfo.has_online_store ? 'Да' : 'Нет'}</div>
              </div>
            )}
            {websiteInfo.target_audience && (
              <div className="detail-item">
                <div className="detail-label">Целевая аудитория</div>
                <div className="detail-value">{websiteInfo.target_audience}</div>
              </div>
            )}
            {websiteInfo.last_crawled && (
              <div className="detail-item">
                <div className="detail-label">Последнее сканирование</div>
                <div className="detail-value">{formatDate(websiteInfo.last_crawled)}</div>
              </div>
            )}
          </div>

          {websiteInfo.contact_info && (
            <div className="detail-item">
              <div className="detail-label">Контакты с сайта</div>
              <div className="detail-value">
                {websiteInfo.contact_info.phone && (
                  <div>📞 {websiteInfo.contact_info.phone}</div>
                )}
                {websiteInfo.contact_info.email && (
                  <div>📧 {websiteInfo.contact_info.email}</div>
                )}
              </div>
            </div>
          )}
        </AccordionItem>

        {/* Дополнительная информация */}
        {(websiteInfo.keywords?.length > 0 || websiteInfo.products_services?.length > 0 ||
          websiteInfo.categories?.length > 0 || websiteInfo.social_media?.length > 0) && (
            <AccordionItem id="website-additional" title="📝 Дополнительная информация">
              {websiteInfo.keywords && websiteInfo.keywords.length > 0 && (
                <div className="detail-item">
                  <div className="detail-label">Ключевые слова</div>
                  <div className="detail-value">
                    <div className="company-categories">
                      {websiteInfo.keywords.map((keyword, idx) => (
                        <span key={idx} className="category-tag">{keyword}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {websiteInfo.products_services && websiteInfo.products_services.length > 0 && (
                <div className="detail-item">
                  <div className="detail-label">Товары и услуги</div>
                  <div className="detail-value">
                    <div className="company-categories">
                      {websiteInfo.products_services.map((product, idx) => (
                        <span key={idx} className="category-tag">{product}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {websiteInfo.categories && websiteInfo.categories.length > 0 && (
                <div className="detail-item">
                  <div className="detail-label">Категории с сайта</div>
                  <div className="detail-value">
                    <div className="company-categories">
                      {websiteInfo.categories.map((category, idx) => (
                        <span key={idx} className="category-tag">{category}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {websiteInfo.social_media && websiteInfo.social_media.length > 0 && (
                <div className="detail-item">
                  <div className="detail-label">Социальные сети</div>
                  <div className="detail-value">
                    {websiteInfo.social_media.map((social, idx) => (
                      <div key={idx}>
                        <a href={social} target="_blank" rel="noopener noreferrer">
                          {social}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </AccordionItem>
          )}
      </div>
    );
  };

  const renderAnalysis = (analysis) => {
    if (!analysis) return null;

    return (
      <div className="detail-section">
        <h3>📊 Анализ клиента</h3>

        {/* Краткое описание */}
        {analysis.summary && (
          <AccordionItem id="summary" title="📋 Краткое описание">
            <div className="summary-text">{cleanMarkdown(analysis.summary)}</div>
          </AccordionItem>
        )}

        {/* Оценка потенциала */}
        {analysis.score && (
          <AccordionItem id="score" title="⭐ Оценка потенциала">
            <div className="score-display">
              <span
                className="score-number"
                style={{ color: analysis.score >= 80 ? '#27ae60' : analysis.score >= 60 ? '#f39c12' : '#e74c3c' }}
              >
                {analysis.score}
              </span>
              <span className="score-total">/100</span>
            </div>
            <div className="score-bar">
              <div
                className="score-fill"
                style={{
                  width: `${analysis.score}%`,
                  backgroundColor: analysis.score >= 80 ? '#27ae60' : analysis.score >= 60 ? '#f39c12' : '#e74c3c'
                }}
              ></div>
            </div>
          </AccordionItem>
        )}

        {/* Ключевые инсайты */}
        {analysis.insights && analysis.insights.length > 0 && (
          <AccordionItem id="insights" title="💡 Ключевые инсайты">
            <div className="insights-grid">
              {analysis.insights.map((insight, idx) => (
                <div key={idx} className="insight-item">
                  <div className="insight-icon">🔹</div>
                  <div className="insight-text">{cleanMarkdown(insight)}</div>
                </div>
              ))}
            </div>
          </AccordionItem>
        )}

        {/* Рекомендации */}
        {analysis.recommendations && analysis.recommendations.length > 0 && (
          <AccordionItem id="recommendations" title="🎯 Рекомендации">
            <div className="recommendations-list">
              {analysis.recommendations.map((recommendation, idx) => (
                <div key={idx} className="recommendation-item">
                  <div className="recommendation-number">{idx + 1}</div>
                  <div className="recommendation-text">{cleanMarkdown(recommendation)}</div>
                </div>
              ))}
            </div>
          </AccordionItem>
        )}

        {/* Метаданные */}
        {analysis.metadata && (
          <AccordionItem id="metadata" title="📈 Метаданные анализа">
            <div className="metadata-grid">
              {analysis.metadata.analysis_date && (
                <div className="metadata-item">
                  <span className="metadata-label">📅 Дата анализа:</span>
                  <span className="metadata-value">{analysis.metadata.analysis_date}</span>
                </div>
              )}
              {analysis.metadata.model_version && (
                <div className="metadata-item">
                  <span className="metadata-label">🔢 Версия модели:</span>
                  <span className="metadata-value">{analysis.metadata.model_version}</span>
                </div>
              )}
              {analysis.metadata.confidence && (
                <div className="metadata-item">
                  <span className="metadata-label">🎯 Достоверность:</span>
                  <span className="metadata-value">{(analysis.metadata.confidence * 100).toFixed(0)}%</span>
                </div>
              )}
              {analysis.metadata.analysis_focus && (
                <div className="metadata-item">
                  <span className="metadata-label">🔍 Фокус анализа:</span>
                  <span className="metadata-value">{analysis.metadata.analysis_focus}</span>
                </div>
              )}
              {analysis.generated_at && (
                <div className="metadata-item">
                  <span className="metadata-label">⏰ Дата создания:</span>
                  <span className="metadata-value">{formatDate(analysis.generated_at)}</span>
                </div>
              )}
            </div>
          </AccordionItem>
        )}
      </div>
    );
  };

  const renderStructuredProfile = (structured_profile) => {
    if (!structured_profile || !structured_profile.detailed_analysis) return null;

    const profile = structured_profile.detailed_analysis;

    return (
      <div className="detail-section">
        <h3>🎯 Структурированный профиль клиента</h3>

        {/* Потенциал клиента */}
        {profile.client_potential && (
          <AccordionItem id="client-potential" title="💼 Потенциал клиента">
            {profile.client_potential.potential_score && (
              <div className="potential-score">
                <span className="potential-label">Потенциал:</span>
                <span
                  className="potential-value"
                  style={{ color: profile.client_potential.potential_score >= 80 ? '#27ae60' : '#f39c12' }}
                >
                  {profile.client_potential.potential_score}/100
                </span>
              </div>
            )}

            {profile.client_potential.budget_indicators && (
              <div className="strategy-item">
                <h5>💰 Бюджетные показатели</h5>
                <p>{cleanMarkdown(profile.client_potential.budget_indicators)}</p>
              </div>
            )}

            {profile.client_potential.decision_makers && profile.client_potential.decision_makers.length > 0 && (
              <div className="strategy-item">
                <h5>👥 Лица, принимающие решения</h5>
                <div className="strategy-list">
                  {profile.client_potential.decision_makers.map((dm, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-bullet">•</span>
                      <span>{cleanMarkdown(dm)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profile.client_potential.business_needs && profile.client_potential.business_needs.length > 0 && (
              <div className="strategy-item">
                <h5>🎯 Бизнес-потребности</h5>
                <div className="strategy-list">
                  {profile.client_potential.business_needs.map((need, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-bullet">•</span>
                      <span>{cleanMarkdown(need)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AccordionItem>
        )}

        {/* Стратегия взаимодействия */}
        {profile.engagement_strategy && (
          <AccordionItem id="engagement-strategy" title="📈 Стратегия взаимодействия">
            {profile.engagement_strategy.approach_tactics && profile.engagement_strategy.approach_tactics.length > 0 && (
              <div className="strategy-item">
                <h5>🎪 Тактики подхода</h5>
                <div className="strategy-list">
                  {profile.engagement_strategy.approach_tactics.map((tactic, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-number">{idx + 1}</span>
                      <span>{cleanMarkdown(tactic)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profile.engagement_strategy.value_propositions && profile.engagement_strategy.value_propositions.length > 0 && (
              <div className="strategy-item">
                <h5>💎 Ценностные предложения</h5>
                <div className="strategy-list">
                  {profile.engagement_strategy.value_propositions.map((prop, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-icon">💡</span>
                      <span>{cleanMarkdown(prop)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profile.engagement_strategy.communication_channels && profile.engagement_strategy.communication_channels.length > 0 && (
              <div className="strategy-item">
                <h5>📢 Каналы коммуникации</h5>
                <div className="strategy-list">
                  {profile.engagement_strategy.communication_channels.map((channel, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-icon">📞</span>
                      <span>{cleanMarkdown(channel)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profile.engagement_strategy.timing_recommendations && (
              <div className="strategy-item">
                <h5>⏰ Рекомендации по времени</h5>
                <p>{cleanMarkdown(profile.engagement_strategy.timing_recommendations)}</p>
              </div>
            )}
          </AccordionItem>
        )}

        {/* Инсайты о компании */}
        {profile.company_insights && (
          <AccordionItem id="company-insights" title="🏢 Инсайты о компании">
            {profile.company_insights.business_model && (
              <div className="strategy-item">
                <h5>🏗️ Бизнес-модель</h5>
                <p>{cleanMarkdown(profile.company_insights.business_model)}</p>
              </div>
            )}

            {profile.company_insights.competitive_position && (
              <div className="strategy-item">
                <h5>🏆 Конкурентная позиция</h5>
                <p>{cleanMarkdown(profile.company_insights.competitive_position)}</p>
              </div>
            )}

            {profile.company_insights.growth_indicators && profile.company_insights.growth_indicators.length > 0 && (
              <div className="strategy-item">
                <h5>📈 Индикаторы роста</h5>
                <div className="strategy-list">
                  {profile.company_insights.growth_indicators.map((indicator, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-icon">📊</span>
                      <span>{cleanMarkdown(indicator)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {profile.company_insights.pain_points && profile.company_insights.pain_points.length > 0 && (
              <div className="strategy-item">
                <h5>🚩 Болевые точки</h5>
                <div className="strategy-list">
                  {profile.company_insights.pain_points.map((pain, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-icon">⚠️</span>
                      <span>{cleanMarkdown(pain)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AccordionItem>
        )}

        {/* Оценка рисков */}
        {profile.risk_assessment && (
          <AccordionItem id="risk-assessment" title="⚠️ Оценка рисков">
            {profile.risk_assessment.financial_stability && (
              <div className="strategy-item">
                <h5>💰 Финансовая стабильность</h5>
                <p>{cleanMarkdown(profile.risk_assessment.financial_stability)}</p>
              </div>
            )}

            {profile.risk_assessment.opportunity_window && (
              <div className="strategy-item">
                <h5>🚪 Окно возможностей</h5>
                <p>{cleanMarkdown(profile.risk_assessment.opportunity_window)}</p>
              </div>
            )}

            {profile.risk_assessment.collaboration_risks && profile.risk_assessment.collaboration_risks.length > 0 && (
              <div className="strategy-item">
                <h5>⚠️ Риски сотрудничества</h5>
                <div className="strategy-list">
                  {profile.risk_assessment.collaboration_risks.map((risk, idx) => (
                    <div key={idx} className="strategy-list-item">
                      <span className="list-icon">🚨</span>
                      <span>{cleanMarkdown(risk)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </AccordionItem>
        )}

        {/* План действий */}
        {profile.action_plan && profile.action_plan.length > 0 && (
          <AccordionItem id="action-plan" title="📋 План действий">
            <div className="action-plan-list">
              {profile.action_plan.map((action, idx) => (
                <div key={idx} className="action-item">
                  <div className="action-number">{idx + 1}</div>
                  <div className="action-text">{cleanMarkdown(action)}</div>
                </div>
              ))}
            </div>
          </AccordionItem>
        )}

        {/* Уровень приоритета */}
        {profile.priority_level && (
          <div className="strategy-card priority-card">
            <h4>🎯 Уровень приоритета</h4>
            <div className={`priority-badge priority-${profile.priority_level}`}>
              <span className="priority-icon">
                {profile.priority_level === 'high' ? '🔥' :
                  profile.priority_level === 'medium' ? '⚡' : '📌'}
              </span>
              <span className="priority-text">
                {profile.priority_level === 'high' ? 'ВЫСОКИЙ ПРИОРИТЕТ' :
                  profile.priority_level === 'medium' ? 'СРЕДНИЙ ПРИОРИТЕТ' : 'НИЗКИЙ ПРИОРИТЕТ'}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTechnicalInfo = (company) => {
    return (
      <div className="detail-section">
        <h3>⚙️ Техническая информация</h3>
        <div className="detail-grid">
          {company.id && (
            <div className="detail-item">
              <div className="detail-label">ID</div>
              <div className="detail-value">{company.id}</div>
            </div>
          )}
          {company.created_at && (
            <div className="detail-item">
              <div className="detail-label">Дата создания записи</div>
              <div className="detail-value">{formatDate(company.created_at)}</div>
            </div>
          )}
          {company.updated_at && (
            <div className="detail-item">
              <div className="detail-label">Дата обновления записи</div>
              <div className="detail-value">{formatDate(company.updated_at)}</div>
            </div>
          )}
        </div>

        {company.social_media && company.social_media.length > 0 && (
          <div className="detail-item">
            <div className="detail-label">Социальные сети</div>
            <div className="detail-value">
              {company.social_media.map((social, idx) => (
                <div key={idx}>
                  <a href={social} target="_blank" rel="noopener noreferrer">
                    {social}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {company.catalog_parsing_results && (
          <div className="detail-item">
            <div className="detail-label">Результаты парсинга каталога</div>
            <div className="detail-value">
              <pre style={{
                background: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                fontSize: '0.8em',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                {JSON.stringify(company.catalog_parsing_results, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getTabCount = (tabId) => {
    switch (tabId) {
      case 'basic':
        return [company.name, company.inn, company.address, company.actual_address, company.working_hours, company.categories].filter(Boolean).length;
      case 'contacts':
        return [
          company.phone_numbers?.length > 0,
          company.website,
          company.website_info?.contact_info?.email,
          company.website_info?.contact_info?.phone,
          company.working_hours,
          company.social_media?.length > 0,
          company.website_info?.social_media?.length > 0,
          company.actual_address,
          company.coordinates?.length === 2
        ].filter(Boolean).length;
      case 'legal':
        return company.legal_info ? Object.values(company.legal_info).filter(Boolean).length : 0;
      case 'website': {
        const wi = company.website_info;
        const cr = company.catalog_parsing_results;
        if (wi) return Object.values(wi).filter(Boolean).length;
        if (cr?.website_url) return 1;
        return 0;
      }
      case 'analysis':
        return company.analysis ? [company.analysis.summary, company.analysis.insights?.length > 0, company.analysis.recommendations?.length > 0].filter(Boolean).length : 0;
      case 'strategy':
        return company.analysis?.structured_profile ? 1 : 0;
      case 'technical':
        return [company.created_at, company.updated_at, company.social_media?.length > 0].filter(Boolean).length;
      default:
        return 0;
    }
  };

  const tabs = [
    { id: 'basic', label: '🏢 Основная информация', icon: '🏢', count: getTabCount('basic') },
    { id: 'contacts', label: '📞 Контакты', icon: '📞', count: getTabCount('contacts') },
    { id: 'legal', label: '📋 Юридические данные', icon: '📋', count: getTabCount('legal') },
    { id: 'website', label: '🌐 Веб-сайт', icon: '🌐', count: getTabCount('website') },
    { id: 'analysis', label: '📊 Анализ клиента', icon: '📊', count: getTabCount('analysis') },
    { id: 'strategy', label: '🎯 Стратегия', icon: '🎯', count: getTabCount('strategy') },
    { id: 'technical', label: '⚙️ Техническая информация', icon: '⚙️', count: getTabCount('technical') }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        const { phone: mainPhone, hours: workingHours, categories: companyCategories } = getCompanyData(company);

        return (
          <div>
            {/* Основная информация */}
            <div className="detail-section">
              <h3>🏢 Основная информация</h3>
              <div className="detail-grid">
                {company.name && (
                  <div className="detail-item">
                    <div className="detail-label">Название</div>
                    <div className="detail-value">{company.name}</div>
                  </div>
                )}
                {company.short_name && (
                  <div className="detail-item">
                    <div className="detail-label">Краткое название</div>
                    <div className="detail-value">{company.short_name}</div>
                  </div>
                )}
                {company.inn && (
                  <div className="detail-item">
                    <div className="detail-label">ИНН</div>
                    <div className="detail-value">{company.inn}</div>
                  </div>
                )}
                {company.source && (
                  <div className="detail-item">
                    <div className="detail-label">Источник данных</div>
                    <div className="detail-value">{company.source}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Адресная информация */}
            <div className="detail-section">
              <h3>📍 Адресная информация</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Юридический адрес</div>
                  <div className="detail-value">{renderAddress(company.address)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Фактический адрес</div>
                  <div className="detail-value">{renderAddress(company.actual_address)}</div>
                </div>
                {company.coordinates && (
                  <div className="detail-item">
                    <div className="detail-label">Координаты</div>
                    <div className="detail-value">
                      <span className="coordinates">{formatCoordinates(company.coordinates)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Рабочая информация */}
            <div className="detail-section">
              <h3>⏰ Рабочая информация</h3>
              <div className="detail-grid">
                {workingHours && (
                  <div className="detail-item">
                    <div className="detail-label">Режим работы</div>
                    <div className="detail-value">{workingHours}</div>
                  </div>
                )}
                {company.description && (
                  <div className="detail-item">
                    <div className="detail-label">Описание</div>
                    <div className="detail-value">{company.description}</div>
                  </div>
                )}
                {company.rating && (
                  <div className="detail-item">
                    <div className="detail-label">Рейтинг</div>
                    <div className="detail-value">⭐ {company.rating}</div>
                  </div>
                )}
                {company.reviews_count && (
                  <div className="detail-item">
                    <div className="detail-label">Количество отзывов</div>
                    <div className="detail-value">{company.reviews_count}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Категории деятельности */}
            {companyCategories && companyCategories.length > 0 && (
              <div className="detail-section">
                <h3>🏷️ Категории деятельности</h3>
                <div className="company-categories">
                  {companyCategories.map((category, idx) => (
                    <span key={idx} className="category-tag">{category}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'contacts':
        const { phone: contactPhone } = getCompanyData(company);

        return (
          <div>
            {/* Основные контакты */}
            <div className="detail-section">
              <h3>📞 Основные контакты</h3>
              <div className="detail-grid">
                {contactPhone && (
                  <div className="detail-item">
                    <div className="detail-label">📱 Телефоны</div>
                    <div className="detail-value">
                      <div className="contact-list">
                        <div className="contact-item">
                          <a href={`tel:${contactPhone.replace(/[^\d+]/g, '')}`} className="contact-link">
                            📞 {contactPhone}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {company.website && (
                  <div className="detail-item">
                    <div className="detail-label">🌐 Веб-сайт</div>
                    <div className="detail-value">
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="contact-link">
                        🔗 {company.website}
                      </a>
                    </div>
                  </div>
                )}

                {company.website_info?.contact_info?.email && (
                  <div className="detail-item">
                    <div className="detail-label">📧 Email</div>
                    <div className="detail-value">
                      <a href={`mailto:${company.website_info.contact_info.email}`} className="contact-link">
                        ✉️ {company.website_info.contact_info.email}
                      </a>
                    </div>
                  </div>
                )}

                {company.website_info?.contact_info?.phone && (
                  <div className="detail-item">
                    <div className="detail-label">📞 Дополнительные телефоны (с сайта)</div>
                    <div className="detail-value">
                      <div className="contact-item">
                        <a href={`tel:${company.website_info.contact_info.phone.replace(/[^\d+]/g, '')}`} className="contact-link">
                          📞 {company.website_info.contact_info.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {company.working_hours && (
                  <div className="detail-item">
                    <div className="detail-label">🕒 Время работы</div>
                    <div className="detail-value">
                      <div className="working-hours-display">
                        ⏰ {company.working_hours}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Социальные сети */}
            {((company.social_media && company.social_media.length > 0) ||
              (company.website_info?.social_media && company.website_info.social_media.length > 0)) && (
                <div className="detail-section">
                  <h3>🌐 Социальные сети и мессенджеры</h3>

                  <div className="social-media-grid">
                    {/* Основные социальные сети */}
                    {company.social_media?.map((link, index) => {
                      const getSocialInfo = (url) => {
                        const lowerUrl = url.toLowerCase();
                        if (lowerUrl.includes('vk.com') || lowerUrl.includes('vkontakte')) {
                          return { name: 'VKontakte', icon: '📘', color: '#0077FF' };
                        } else if (lowerUrl.includes('telegram') || lowerUrl.includes('t.me')) {
                          return { name: 'Telegram', icon: '📱', color: '#0088CC' };
                        } else if (lowerUrl.includes('instagram') || lowerUrl.includes('insta')) {
                          return { name: 'Instagram', icon: '📷', color: '#E4405F' };
                        } else if (lowerUrl.includes('facebook') || lowerUrl.includes('fb.')) {
                          return { name: 'Facebook', icon: '👤', color: '#1877F2' };
                        } else if (lowerUrl.includes('youtube') || lowerUrl.includes('youtu.be')) {
                          return { name: 'YouTube', icon: '🎥', color: '#FF0000' };
                        } else if (lowerUrl.includes('whatsapp') || lowerUrl.includes('wa.me')) {
                          return { name: 'WhatsApp', icon: '💬', color: '#25D366' };
                        } else if (lowerUrl.includes('twitter') || lowerUrl.includes('x.com')) {
                          return { name: 'Twitter/X', icon: '🐦', color: '#1DA1F2' };
                        } else if (lowerUrl.includes('linkedin')) {
                          return { name: 'LinkedIn', icon: '💼', color: '#0A66C2' };
                        } else if (lowerUrl.includes('ok.ru') || lowerUrl.includes('odnoklassniki')) {
                          return { name: 'Одноклассники', icon: '🟠', color: '#FF6600' };
                        } else if (lowerUrl.startsWith('+') || lowerUrl.match(/^\d/)) {
                          return { name: 'Телефон', icon: '📞', color: '#28A745' };
                        } else {
                          return { name: 'Ссылка', icon: '🔗', color: '#6C757D' };
                        }
                      };

                      const socialInfo = getSocialInfo(link);
                      const cleanUrl = link.startsWith('http') ? link : `https://${link}`;
                      const displayUrl = link.length > 50 ? link.substring(0, 47) + '...' : link;

                      return (
                        <div key={`main-${index}`} className="social-card">
                          <div className="social-header">
                            <span className="social-icon" style={{ color: socialInfo.color }}>
                              {socialInfo.icon}
                            </span>
                            <span className="social-name">{socialInfo.name}</span>
                          </div>
                          <div className="social-link">
                            <a
                              href={link.startsWith('+') ? `tel:${link}` : cleanUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="social-url"
                            >
                              {displayUrl}
                            </a>
                          </div>
                        </div>
                      );
                    })}

                    {/* Социальные сети с сайта */}
                    {company.website_info?.social_media?.map((link, index) => {
                      const getSocialInfo = (url) => {
                        const lowerUrl = url.toLowerCase();
                        if (lowerUrl.includes('vk.com') || lowerUrl.includes('vkontakte')) {
                          return { name: 'VKontakte', icon: '📘', color: '#0077FF' };
                        } else if (lowerUrl.includes('telegram') || lowerUrl.includes('t.me')) {
                          return { name: 'Telegram', icon: '📱', color: '#0088CC' };
                        } else if (lowerUrl.includes('instagram') || lowerUrl.includes('insta')) {
                          return { name: 'Instagram', icon: '📷', color: '#E4405F' };
                        } else if (lowerUrl.includes('facebook') || lowerUrl.includes('fb.')) {
                          return { name: 'Facebook', icon: '👤', color: '#1877F2' };
                        } else if (lowerUrl.includes('youtube') || lowerUrl.includes('youtu.be')) {
                          return { name: 'YouTube', icon: '🎥', color: '#FF0000' };
                        } else if (lowerUrl.includes('whatsapp') || lowerUrl.includes('wa.me')) {
                          return { name: 'WhatsApp', icon: '💬', color: '#25D366' };
                        } else if (lowerUrl.includes('twitter') || lowerUrl.includes('x.com')) {
                          return { name: 'Twitter/X', icon: '🐦', color: '#1DA1F2' };
                        } else if (lowerUrl.includes('linkedin')) {
                          return { name: 'LinkedIn', icon: '💼', color: '#0A66C2' };
                        } else if (lowerUrl.includes('ok.ru') || lowerUrl.includes('odnoklassniki')) {
                          return { name: 'Одноклассники', icon: '🟠', color: '#FF6600' };
                        } else if (lowerUrl.startsWith('+') || lowerUrl.match(/^\d/)) {
                          return { name: 'Телефон', icon: '📞', color: '#28A745' };
                        } else {
                          return { name: 'Ссылка', icon: '🔗', color: '#6C757D' };
                        }
                      };

                      const socialInfo = getSocialInfo(link);
                      const cleanUrl = link.startsWith('http') ? link : `https://${link}`;
                      const displayUrl = link.length > 50 ? link.substring(0, 47) + '...' : link;

                      return (
                        <div key={`web-${index}`} className="social-card">
                          <div className="social-header">
                            <span className="social-icon" style={{ color: socialInfo.color }}>
                              {socialInfo.icon}
                            </span>
                            <span className="social-name">{socialInfo.name}</span>
                            <span className="social-source">(с сайта)</span>
                          </div>
                          <div className="social-link">
                            <a
                              href={link.startsWith('+') ? `tel:${link}` : cleanUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="social-url"
                            >
                              {displayUrl}
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Адрес и местоположение */}
            <div className="detail-section">
              <h3>📍 Местоположение</h3>
              <div className="detail-grid">
                {company.actual_address && (() => {
                  const addressData = createClickableAddressData(
                    company.actual_address,
                    company.coordinates && company.coordinates.length === 2
                      ? { lat: company.coordinates[0], lon: company.coordinates[1] }
                      : null
                  );

                  // Отладочная информация


                  return (
                    <div className="detail-item">
                      <div className="detail-label">📌 Фактический адрес</div>
                      <div className="detail-value">
                        <div className="address-display">
                          {addressData.isClickable ? (
                            <a
                              href={addressData.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={addressData.className}
                              title="Открыть на Яндекс.Картах"
                              style={{ color: '#1976d2', textDecoration: 'underline' }}
                            >
                              📍 {addressData.text}
                            </a>
                          ) : (
                            <>📍 {addressData.text}</>
                          )}
                          {company.actual_address && typeof company.actual_address === 'object' && company.actual_address.postal_code && (
                            <div className="postal-code">📮 {company.actual_address.postal_code}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {company.address && company.address !== company.actual_address && (() => {
                  const addressData = createClickableAddressData(company.address);

                  return (
                    <div className="detail-item">
                      <div className="detail-label">🏛️ Юридический адрес</div>
                      <div className="detail-value">
                        <div className="address-display">
                          {addressData.isClickable ? (
                            <a
                              href={addressData.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={addressData.className}
                              title="Открыть на Яндекс.Картах"
                            >
                              📍 {addressData.text}
                            </a>
                          ) : (
                            <>📍 {addressData.text}</>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {company.coordinates && company.coordinates.length === 2 && (
                  <div className="detail-item">
                    <div className="detail-label">🗺️ Координаты</div>
                    <div className="detail-value">
                      <div className="coordinates-display">
                        🎯 {company.coordinates[0]}, {company.coordinates[1]}
                        <a
                          href={`https://maps.yandex.ru/?pt=${company.coordinates[1]},${company.coordinates[0]}&z=16&l=map`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="map-link"
                        >
                          📍 Открыть на карте
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'legal':
        return renderLegalInfo(company.legal_info);

      case 'website':
        return renderWebsiteInfo(company.website_info, company.catalog_parsing_results);

      case 'analysis':
        return renderAnalysis(company.analysis);

      case 'strategy':
        return renderStructuredProfile(company.analysis?.structured_profile);

      case 'technical':
        return renderTechnicalInfo(company);

      default:
        return null;
    }
  };

  return (
    <div className="container">
      <button className="back-button" onClick={() => navigate('/companies')}>
        ← Вернуться к списку
      </button>

      <div className="detail-page">
        <div className="company-header">
          <div className="company-header-left">
            <h1>{company.name || 'Название не указано'}</h1>
            <div className="company-header-actions">
              <button
                className="export-to-1c-btn"
                onClick={handleExportTo1C}
                disabled={isExporting}
              >
                {isExporting ? 'Экспортируем...' : 'Экспорт в CRM'}
              </button>
              {statusIdentifier && (
                statusInfo ? (
                  <Chip
                    icon={<statusInfo.MuiIcon style={{ color: statusInfo.color }} />}
                    label={statusInfo.label}
                    deleteIcon={<ArrowDownIcon />}
                    onDelete={handleStatusButtonClick}
                    onClick={handleStatusButtonClick}
                    disabled={isSavingStatus}
                    sx={{
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.color,
                      fontWeight: 500,
                      border: `1px solid ${statusInfo.color}`,
                      cursor: 'pointer',
                      '& .MuiChip-deleteIcon': { color: statusInfo.color },
                      '&:hover': {
                        backgroundColor: statusInfo.bgColor,
                        filter: 'brightness(0.95)',
                      },
                    }}
                  />
                ) : (
                  <Chip
                    icon={<AddStatusIcon />}
                    label="Установить статус"
                    onClick={handleStatusButtonClick}
                    disabled={isSavingStatus}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderColor: '#1565c0',
                      },
                    }}
                  />
                )
              )}
            </div>
            {companyStatus?.comment && (
              <div className="company-status-comment" title={companyStatus.comment}>
                💬 {companyStatus.comment}
              </div>
            )}
          </div>
          {company.analysis?.score && (
            <div className="company-score">
              <span className="score-label">Потенциал:</span>
              <span
                className="score-value"
                style={{
                  color: company.analysis.score >= 80 ? '#27ae60' :
                    company.analysis.score >= 60 ? '#f39c12' : '#e74c3c'
                }}
              >
                {company.analysis.score}/100
              </span>
            </div>
          )}
        </div>

        {/* Быстрая информация */}
        <div className="quick-info">
          {company.inn && <span className="quick-tag">📄 ИНН: {company.inn}</span>}
          {company.working_hours && <span className="quick-tag">🕒 {company.working_hours}</span>}
          {company.phone_numbers && company.phone_numbers[0] && (
            <span className="quick-tag">📞 {company.phone_numbers[0]}</span>
          )}
          {company.analysis?.structured_profile?.detailed_analysis?.priority_level && (
            <span
              className={`quick-tag priority-${company.analysis.structured_profile.detailed_analysis.priority_level}`}
            >
              🚨 {company.analysis.structured_profile.detailed_analysis.priority_level === 'high' ? 'ВЫСОКИЙ ПРИОРИТЕТ' :
                company.analysis.structured_profile.detailed_analysis.priority_level === 'medium' ? 'СРЕДНИЙ ПРИОРИТЕТ' : 'НИЗКИЙ ПРИОРИТЕТ'}
            </span>
          )}
        </div>

        {/* Навигация по вкладкам */}
        <div className="tabs-navigation">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.count === 0 ? 'disabled' : ''}`}
              onClick={() => tab.count > 0 && setActiveTab(tab.id)}
              disabled={tab.count === 0}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
            </button>
          ))}
        </div>

        {/* Содержимое вкладки */}
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      {/* Обычные уведомления */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Меню выбора статуса */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusMenuClose}
      >
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.85em' }}>
          Выберите статус:
        </MenuItem>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <MenuItem
            key={key}
            onClick={() => handleStatusSelect(key)}
            selected={companyStatus?.status === key}
          >
            <ListItemIcon>
              <config.MuiIcon sx={{ color: config.color }} />
            </ListItemIcon>
            <ListItemText>{config.label}</ListItemText>
          </MenuItem>
        ))}

        {companyStatus?.status && [
          <Divider key="status-divider" />,
          <MenuItem key="edit-comment" onClick={() => handleStatusSelect(companyStatus.status)}>
            <ListItemIcon>
              <EditIcon />
            </ListItemIcon>
            <ListItemText>Изменить комментарий</ListItemText>
          </MenuItem>,
          <MenuItem key="remove-status" onClick={handleRemoveStatus} disabled={isSavingStatus}>
            <ListItemIcon>
              <RemoveStatusIcon color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Снять статус</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* Диалог для ввода комментария */}
      <Dialog
        open={statusCommentOpen}
        onClose={() => setStatusCommentOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>
          {selectedStatus && STATUS_CONFIG[selectedStatus] && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {STATUS_CONFIG[selectedStatus].icon} {STATUS_CONFIG[selectedStatus].label}
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Комментарий (необязательно)"
            fullWidth
            multiline
            rows={3}
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            placeholder="Например: Перезвонить после 15:00, интересует электроинструмент..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusCommentOpen(false)} disabled={isSavingStatus}>
            Отмена
          </Button>
          <Button
            onClick={handleSaveStatus}
            variant="contained"
            disabled={isSavingStatus}
            startIcon={isSavingStatus ? <CircularProgress size={16} /> : null}
          >
            {isSavingStatus ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог выбора менеджера */}
      <ManagerSelector
        open={managerSelectorOpen}
        onClose={handleManagerSelectorClose}
        onConfirm={handleManagerSelectorConfirm}
        isExporting={isExporting}
      />
    </div>
  );
};

export default CompanyDetail;
